import { SectionCard } from '@atlas/ui-components'
import { provenanceLine, type RuleResultOutcome, type RuleValidationResultRow } from '@atlas/ui-domain'
import { Badge, Button, cn, Spinner } from '@atlas/ui-primitives'
import { AlertTriangle, CheckCircle2, ChevronRight, CircleDashed, XCircle } from 'lucide-react'

/** One draft's rule verdicts. */
export interface DraftRuleCheckFile {
	fileId: string
	fileName: string
	/** The run could not be set up, so nothing was judged. */
	didFail?: boolean
	checks: RuleValidationResultRow[]
}

export interface DraftRuleChecksProps {
	/** Null until a run over the current drafts has finished. */
	results: readonly DraftRuleCheckFile[] | null
	isRunning: boolean
	draftCount: number
	onValidate: () => void
}

type Tone = 'success' | 'warning' | 'danger' | 'muted'

/**
 * A failed blocking rule reads as danger and a failed warning rule as a warning; neither gates
 * submitting. `needs_review` keeps its own wording rather than passing as a pass.
 */
const toneOf = (check: RuleValidationResultRow): Tone =>
	check.outcome === 'pass'
		? 'success'
		: check.outcome === 'pending'
			? 'muted'
			: check.outcome === 'fail' && check.severity === 'blocking'
				? 'danger'
				: 'warning'

const LABEL: Record<RuleResultOutcome, string> = {
	pass: 'Pass',
	fail: 'Failed',
	needs_review: 'Needs review',
	pending: 'No verdict',
}

const ICON: Record<Tone, typeof CheckCircle2> = {
	success: CheckCircle2,
	warning: AlertTriangle,
	danger: XCircle,
	muted: CircleDashed,
}

const ICON_CLASS: Record<Tone, string> = {
	success: 'text-ds-success',
	warning: 'text-ds-warning',
	danger: 'text-ds-danger',
	muted: 'text-ds-text-subtle',
}

const SEGMENT_CLASS: Record<Tone, string> = {
	success: 'bg-ds-success',
	warning: 'bg-ds-warning',
	danger: 'bg-ds-danger',
	muted: 'bg-ds-border',
}

/** What needs someone first: failures, then reviews, then the rest. Blocking before warning. */
const RANK: Record<RuleResultOutcome, number> = { fail: 0, needs_review: 1, pending: 2, pass: 3 }
const byPriority = (a: RuleValidationResultRow, b: RuleValidationResultRow) =>
	RANK[a.outcome] - RANK[b.outcome] || Number(b.severity === 'blocking') - Number(a.severity === 'blocking')

const needsAttention = (check: RuleValidationResultRow) => check.outcome === 'fail' || check.outcome === 'needs_review'

function tally(checks: readonly RuleValidationResultRow[]): string {
	const count = (outcome: RuleResultOutcome) => checks.filter((c) => c.outcome === outcome).length
	return [
		[count('pass'), 'passed'],
		[count('fail'), 'failed'],
		[count('needs_review'), 'need review'],
		[count('pending'), 'no verdict'],
	]
		.filter(([n]) => n)
		.map(([n, label]) => `${n} ${label}`)
		.join(' · ')
}

const formatTime = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

/** The headline verdict and one strip segment per check, worst first. */
function RunSummary({ checks }: { checks: RuleValidationResultRow[] }) {
	const issues = checks.filter(needsAttention).length
	return (
		<div className="space-y-2 rounded-ds-md bg-ds-surface px-4 py-3">
			<div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
				<p className="text-ds-base font-semibold text-ds-text">
					{issues
						? `${issues} of ${checks.length} ${checks.length === 1 ? 'check needs' : 'checks need'} attention`
						: `All ${checks.length} ${checks.length === 1 ? 'check passes' : 'checks pass'}`}
				</p>
				<p className="text-ds-xs text-ds-text-muted">{tally(checks)}</p>
			</div>
			<div className="flex h-1.5 gap-0.5 overflow-hidden rounded-ds-full" aria-hidden>
				{[...checks].sort(byPriority).map((check) => (
					<span key={check.id} className={cn('flex-1', SEGMENT_CLASS[toneOf(check)])} />
				))}
			</div>
		</div>
	)
}

/** One rule: the verdict at a glance, the reasoning and evidence on expand. */
function CheckRow({ check }: { check: RuleValidationResultRow }) {
	const tone = toneOf(check)
	const Icon = ICON[tone]
	return (
		<li>
			<details className="group">
				<summary className="flex cursor-pointer list-none items-start gap-3 px-3 py-2.5 hover:bg-ds-surface focus-visible:outline-2 focus-visible:outline-ds-primary [&::-webkit-details-marker]:hidden">
					<Icon className={cn('mt-0.5 h-4 w-4 shrink-0', ICON_CLASS[tone])} aria-hidden />
					<span className="min-w-0 flex-1">
						<span className="block text-ds-sm text-ds-text">{check.statement}</span>
						<span className="mt-0.5 block font-mono text-ds-3xs uppercase tracking-wide text-ds-text-subtle">
							{check.severity === 'blocking' ? 'Blocking' : 'Warning'} rule
						</span>
					</span>
					<Badge size="sm" variant={tone}>
						{LABEL[check.outcome]}
					</Badge>
					<ChevronRight
						className="mt-0.5 h-4 w-4 shrink-0 text-ds-text-subtle transition-transform group-open:rotate-90 motion-reduce:transition-none"
						aria-hidden
					/>
				</summary>
				<div className="space-y-2 pb-3 pl-10 pr-3">
					<p className={cn('text-ds-sm', check.rationale ? 'text-ds-text' : 'italic text-ds-text-subtle')}>
						{check.rationale ?? 'No rationale recorded.'}
					</p>
					{check.evidence ? (
						<blockquote className="border-l-2 border-ds-border pl-3 text-ds-xs text-ds-text-muted">
							{check.evidence}
						</blockquote>
					) : null}
					<p className="text-ds-3xs uppercase tracking-wide text-ds-text-subtle">
						{[
							check.elementType,
							provenanceLine(check.provenance),
							check.evaluatedAt ? formatTime(check.evaluatedAt) : null,
						]
							.filter(Boolean)
							.join(' · ')}
					</p>
				</div>
			</details>
		</li>
	)
}

/** A draft's checks, open by default when one of them needs attention. */
function FileChecks({ file }: { file: DraftRuleCheckFile }) {
	const empty = file.didFail ? 'The rules could not be checked for this file.' : 'No rules apply to this file.'
	const issues = file.checks.some(needsAttention)
	return (
		<li className="overflow-hidden rounded-ds-md border border-ds-border">
			<details className="group/file" open={issues || file.didFail}>
				<summary className="flex cursor-pointer list-none items-center gap-2 bg-ds-surface px-3 py-2 focus-visible:outline-2 focus-visible:outline-ds-primary [&::-webkit-details-marker]:hidden">
					<ChevronRight
						className="h-4 w-4 shrink-0 text-ds-text-subtle transition-transform group-open/file:rotate-90 motion-reduce:transition-none"
						aria-hidden
					/>
					<span className="min-w-0 flex-1 truncate text-ds-sm font-medium text-ds-text">{file.fileName}</span>
					<span className={cn('shrink-0 text-ds-xs', file.didFail ? 'text-ds-danger' : 'text-ds-text-muted')}>
						{file.didFail ? 'Not checked' : file.checks.length ? tally(file.checks) : 'No rules'}
					</span>
				</summary>
				{file.checks.length ? (
					<ul className="divide-y divide-ds-border border-t border-ds-border">
						{[...file.checks].sort(byPriority).map((check) => (
							<CheckRow key={check.id} check={check} />
						))}
					</ul>
				) : (
					<p className="border-t border-ds-border px-3 py-2.5 text-ds-sm text-ds-text-muted">{empty}</p>
				)}
			</details>
		</li>
	)
}

/** Validate rules on the drafts: the action, a running state, then the verdict and each draft's checks. */
export function DraftRuleChecks({ results, isRunning, draftCount, onValidate }: DraftRuleChecksProps) {
	const checks = results?.flatMap((file) => file.checks) ?? []
	return (
		<SectionCard
			title="Validate rules"
			aria-label="Validate rules"
			description="Check the drafts against this order's rules. Results are advisory and do not block submitting."
			action={
				<Button size="sm" variant="outline" className="rounded-ds-full" isDisabled={isRunning} onPress={onValidate}>
					{results ? 'Validate again' : 'Validate rules'}
				</Button>
			}
		>
			{isRunning ? (
				<p className="flex items-center gap-2 text-ds-sm text-ds-text-muted">
					<Spinner size="sm" label="Validating" />
					Validating {draftCount === 1 ? '1 file' : `${draftCount} files`}…
				</p>
			) : results ? (
				<div className="space-y-3">
					{checks.length ? <RunSummary checks={checks} /> : null}
					<ul className="space-y-2">
						{results.map((file) => (
							<FileChecks key={file.fileId} file={file} />
						))}
					</ul>
				</div>
			) : (
				<p className="text-ds-sm text-ds-text-muted">Not validated since the last upload.</p>
			)}
		</SectionCard>
	)
}
